import type {
  PhaseBuildExecutionState,
  PhaseBuildTaskOption,
} from '../services/buildPhaseService';

export const isPlanningTaskContractComplete = (
  pmStepStatus: string | null | undefined,
): boolean => (
  pmStepStatus === null
  || pmStepStatus === undefined
  || pmStepStatus === 'done'
  || pmStepStatus === 'skipped'
);

const BLOCKING_TITLES: Partial<Record<
PhaseBuildExecutionState['blocking_reason'] & string,
string
>> = {
  assignment_required: 'Start building to organize this project into build phases.',
  build_in_progress: 'This phase is already building.',
  dependencies: 'Open Phase tasks and repair dependencies assigned to a future phase.',
  needs_attention: 'Open Phase tasks and resolve failed, blocked, paused, or review work.',
};

export const formatPhaseCredits = (value: number | undefined): string => {
  const safeValue = Number.isFinite(value) ? Number(value) : 0;

  return safeValue % 1 === 0
    ? `${safeValue}`
    : safeValue.toFixed(2).replace(/\.?0+$/, '');
};

const phaseTaskTotal = (rawCount: string): string => {
  const count = Number(rawCount);

  return `${count} ${count === 1 ? 'task' : 'tasks'} total`;
};

export const normalizePhaseReadyTaskSummary = (content: string): string => {
  if (!/\bPhase\s+\d+\s+[—-]\s+.+\s+is ready\b/i.test(content)) return content;

  return content
    .replace(
      /\(\d+\s*\/\s*(\d+)\s+tasks?\s+complete\)/gi,
      (_match, total: string) => `with ${phaseTaskTotal(total)}`,
    )
    .replace(
      /\((\d+)\s*:\s*Total tasks\)/gi,
      (_match, total: string) => `with ${phaseTaskTotal(total)}`,
    );
};

const phaseReadyNumber = (content: string): number | null => {
  const match = content.match(/\bPhase\s+(\d+)\s+[—-]\s+.+\s+is ready\b/i);

  return match ? Number(match[1]) : null;
};

export const formatPhaseReadyMessageForState = (
  content: string,
  state: PhaseBuildExecutionState | null,
): string => {
  const normalized = normalizePhaseReadyTaskSummary(content);
  const messagePhaseNumber = phaseReadyNumber(normalized);
  const currentPhaseNumber = state?.current_phase?.phase_number;
  const phaseIsComplete = messagePhaseNumber !== null && Boolean(
    state?.all_completed
    || (currentPhaseNumber && currentPhaseNumber > messagePhaseNumber),
  );

  if (!phaseIsComplete) return normalized;

  return normalized
    .replace(/\bis ready\b/i, 'is complete')
    .replace(/\s+and requires\s+[\d,.]+\s+credits?\./i, '.')
    .replace(/\s+Start Building Phase\s+\d+\s+when ready\./i, '')
    .trim();
};

export const currentPhaseReadyMessage = (
  state: PhaseBuildExecutionState | null,
): string | null => {
  const phase = state?.current_phase;

  if (!phase) {
    return state?.all_completed
      ? 'Review the design and architecture. All build phases are complete.'
      : null;
  }

  const taskLabel = phase.task_count === 1 ? 'task' : 'tasks';

  return (
    'Review the design and architecture. '
    + `Phase ${phase.phase_number} — ${phase.name} is ready `
    + `with ${phase.task_count} ${taskLabel} total `
    + `and requires ${formatPhaseCredits(phase.remaining_credits)} credits. `
    + `Start Building Phase ${phase.phase_number} when ready.`
  );
};

export const createPhaseBuildEventDetail = (
  clickId: string,
  phaseId?: string | null,
  selectedPhaseTaskIds?: string[],
  selectionSummaryId?: string,
): {
  skipNavigation: true;
  clickId: string;
  phaseId: string | null;
  selectedPhaseTaskIds?: string[];
  selectionSummaryId?: string;
} => ({
  skipNavigation: true,
  clickId,
  phaseId: phaseId ?? null,
  selectedPhaseTaskIds,
  selectionSummaryId,
});

const joinNaturally = (items: string[]): string => {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;

  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

export const selectedPhaseOutcome = (tasks: PhaseBuildTaskOption[]): string => {
  if (tasks.length === 0) return 'Select at least one task to define this run.';
  const summaries = tasks
    .slice(0, 3)
    .map((task) => (task.summary || task.title).replace(/[.!?]+$/, ''));
  const suffix = tasks.length > 3 ? `, plus ${tasks.length - 3} more` : '';

  return `${joinNaturally(summaries)}${suffix}.`;
};

const phaseAvailabilityText = (state: PhaseBuildExecutionState): string => {
  const tasks = state.phase_tasks;

  if (!tasks?.length) return 'is ready.';
  const readyCount = tasks.filter((task) => task.selectable !== false).length;
  const unavailableCount = tasks.length - readyCount;

  if (unavailableCount === 0) return 'is ready.';

  return `has ${tasks.length} tasks: ${readyCount} ready now and ${unavailableCount} unavailable.`;
};

export const phaseSelectionPrompt = (
  state: PhaseBuildExecutionState,
  selectedTasks?: PhaseBuildTaskOption[],
): string => {
  const phase = state.current_phase;

  if (!phase) return '';
  const prompt = `Review the design and architecture. Phase ${phase.phase_number} — ${phase.name} `
    + `${phaseAvailabilityText(state)} `
    + 'Select the tasks to run now.';

  if (!selectedTasks) return prompt;
  const outcome = selectedPhaseOutcome(selectedTasks);

  return selectedTasks.length === 0
    ? `${prompt} ${outcome}`
    : `${prompt} This run achieves: ${outcome}`;
};

export const isPhaseResolutionRequired = (
  state: PhaseBuildExecutionState | null,
): boolean => (
  state?.blocking_reason === 'dependencies'
  || state?.blocking_reason === 'needs_attention'
);

export const isPhaseBuildActionEnabled = (
  state: PhaseBuildExecutionState | null,
): boolean => Boolean(
  state?.can_start
  || state?.blocking_reason === 'assignment_required'
  || isPhaseResolutionRequired(state),
);

export const getPhaseBuildLabel = (
  state: PhaseBuildExecutionState | null,
  loading = false,
): string => {
  const phaseNumber = state?.current_phase?.phase_number;

  if (state?.all_completed) return 'All Phases Built';
  if (!phaseNumber) return loading ? 'Loading Build Phase…' : 'Start Building';
  if (state?.blocking_reason === 'build_in_progress') return `Building Phase ${phaseNumber}…`;
  if (
    state?.blocking_reason === 'needs_attention'
    || state?.blocking_reason === 'dependencies'
  ) {
    return `Resolve Phase ${phaseNumber} Tasks`;
  }

  return `Start Building Phase ${phaseNumber}`;
};

export const getPhaseBuildBusyLabel = (
  state: PhaseBuildExecutionState | null,
): string => {
  const phaseNumber = state?.current_phase?.phase_number;

  if (!phaseNumber) return 'Starting Build…';

  return isPhaseResolutionRequired(state)
    ? `Resolving Phase ${phaseNumber}…`
    : `Starting Phase ${phaseNumber}…`;
};

export const getPhaseBuildTitle = (
  state: PhaseBuildExecutionState | null,
): string => {
  if (!state) return 'Loading the next build phase.';
  if (state.all_completed) return 'Every build phase is complete.';
  if (state.credit_shortfall > 0) {
    const partialText = state.can_start
      ? ` ${state.affordable_task_count ?? 0} affordable tasks will start; `
        + `${state.deferred_task_count ?? 0} will move to backlog.`
      : '';

    return (
      `${formatPhaseCredits(state.required_credits)} credits required; `
      + `${formatPhaseCredits(state.available_credits)} available; `
      + `${formatPhaseCredits(state.credit_shortfall)} more needed.`
      + partialText
    );
  }
  const blockingTitle = state.blocking_reason
    ? BLOCKING_TITLES[state.blocking_reason]
    : undefined;

  return blockingTitle
    || `${formatPhaseCredits(state.required_credits)} credits required for this phase.`;
};
